import { EventEmitter, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RestrictPlanService {
  restrictionEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  showModal: boolean = false;

  
  constructor() { }


  isFeatureRestricted(featureKey: string): boolean {
    return localStorage.getItem(featureKey) === "false";
  }

    /**
   * Handles feature access:
   * - If restricted, show modal.
   * - Otherwise, execute the function.
   */
    handleFeatureAccess(featureKey: string, callback: Function) {
      if (this.isFeatureRestricted(featureKey)) {
        this.restrictionEvent.emit(true);  // Show modal
      } else {
        callback();  // Execute allowed function
      }
    }

    showUpgradeModal() {
      console.log("    showUpgradeModal------------");
      this.showModal = true;
    }
  
    closeModal() {
      this.showModal = false;
    }

    // ------------------------------------------------------------------------------------------
    // Lead-only plan
    // The Lead Dashboard is for every centre. `leadOnlyAccess: true` (from the backend) means the
    // centre's only active plan is a lead-only plan (no CRM plan): its users get the Lead
    // Dashboard and their own Profile page (device features only if the branch has a biometric
    // device) - plus, for Admins, the Profile page's Branches, Company Details, Subscription and
    // Feedbacks sections. A centre with a CRM plan keeps the full panel.
    // The flag arrives with the logged-in user's details (login response) and is refreshed on
    // every app load by the checkAccess call (side-nav.component.ts), so assigning, changing or
    // removing the plan takes effect without code changes.
    // This is UX only - the backend enforces the same rules on every API request.
    // ------------------------------------------------------------------------------------------

    static readonly LEAD_ONLY_HOME = '/gym/dashboard/lead';
    // Exact URLs (path only, no params / sub-routes) a lead-only user may open.
    static readonly LEAD_ONLY_ALLOWED_URLS = ['/gym/dashboard/lead', '/gym/profile'];

    // Extra pages a lead-only ADMIN may open - the Profile page's Branches, Company Details,
    // Subscription and Feedbacks sections and their own sub-pages (backend allows the matching
    // APIs for Admins only, see leadOnlyPlan.middleware.js).
    static readonly LEAD_ONLY_ADMIN_URL_PATTERNS: RegExp[] = [
      /^\/gym\/profile\/(company-details|update-brand)$/,
      /^\/gym\/branches$/,
      /^\/gym\/branches\/branch\/[^/]+$/,
      /^\/gym\/branches\/(updateBranch|centerdetails|centertiming|centeraddress|centerservices)\/[^/]+$/
    ];

    private readUserDetails(): any {
      try {
        return JSON.parse(localStorage.getItem('userDetails') || 'null');
      } catch (e) {
        return null;
      }
    }

    isLeadOnlyPlan(): boolean {
      const userDetails = this.readUserDetails();
      return !!(userDetails && userDetails.leadOnlyAccess === true);
    }

    // Lead-only Admin: also gets the Branches / Company Details / Subscription / Feedbacks sections.
    // Lead Dashboard entry points (top-bar Leads tab, "Open Lead Dashboard" in the expired pop-up):
    // lead-only gyms always; otherwise Admins only - every Admin when environment.showLeadDashboard
    // is on, before that only the Admin logins listed in environment.leadDashboardEmails (our own account).
    canSeeLeadDashboard(): boolean {
      if (this.isLeadOnlyPlan()) return true;
      const user = this.readUserDetails();
      if (!user || user.role !== 'Admin') return false;
      if ((environment as any).showLeadDashboard) return true;
      const email = String(user.emailId || user.email || '').trim().toLowerCase();
      const allowed: string[] = (environment as any).leadDashboardEmails || [];
      return !!email && allowed.some(e => String(e).trim().toLowerCase() === email);
    }

    isLeadOnlyAdmin(): boolean {
      const userDetails = this.readUserDetails();
      return !!(userDetails && userDetails.leadOnlyAccess === true && userDetails.role === 'Admin');
    }

    isLeadOnlyUrlAllowed(url: string): boolean {
      const path = (url || '').split(/[?#;]/)[0].replace(/\/+$/, '');
      if (RestrictPlanService.LEAD_ONLY_ALLOWED_URLS.includes(path)) return true;
      return this.isLeadOnlyAdmin() && RestrictPlanService.LEAD_ONLY_ADMIN_URL_PATTERNS.some(re => re.test(path));
    }

    // true when the current user is on the lead-only plan and `url` is outside what it allows.
    isUrlBlockedForLeadOnly(url: string): boolean {
      return this.isLeadOnlyPlan() && !this.isLeadOnlyUrlAllowed(url);
    }

    // Keeps the stored flag in sync with the backend (called with the checkAccess response).
    syncLeadOnlyAccess(leadOnlyAccess: any): void {
      if (typeof leadOnlyAccess !== 'boolean') return;
      try {
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || 'null');
        if (!userDetails || userDetails.leadOnlyAccess === leadOnlyAccess) return;
        userDetails.leadOnlyAccess = leadOnlyAccess;
        localStorage.setItem('userDetails', JSON.stringify(userDetails));
      } catch (e) { }
    }

}
